import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Link, Form, useNavigate } from "react-router";
import toast from "react-hot-toast";

import { useAuthStore } from "../store/authStore";
import { useRecordStore } from "../store/recordStore";
import TagSelectDropdown from "./TagSelectDropdown.jsx";
import StandardRefsDropdown from "./StandardRefsDropdown.jsx";
import api, { ASSET_BASE_URL } from "../utilities/api";
import { recordsIdentical } from "../utilities/recordDiff";
import { recordPropType } from "../utilities/recordPropTypes";

export default function FieldDetail({ method, field }) {
  const isAdmin = useAuthStore((state) => state.isAdmin);
  const isMember = useAuthStore((state) => state.isMember);
  const navigate = useNavigate();

  // Draft existence state
  const [draftExists, setDraftExists] = useState(false);

  // Get everything from store
  const fields = useRecordStore((state) => state.fields);

  // Get current record
  const currentRecord = fields.findIndex((f) => f && f._id === field._id);
  const getId = (idx) => fields[idx]?._id;

  // Zustand setters
  const setCurrentRecord = useRecordStore((state) => state.setCurrentRecord);

  // Navigation handlers
  const goToFirst = () => navigate(`../${getId(0)}`);
  const goToPrev = () => navigate(`../${getId(currentRecord - 1)}`);
  const goToNext = () => navigate(`../${getId(currentRecord + 1)}`);
  const goToLast = () => navigate(`../${getId(fields.length - 1)}`);

  // Store current record in Zustand store
  useEffect(() => {
    setCurrentRecord(currentRecord);
  }, [currentRecord, setCurrentRecord]);

  // Check if a draft exists for this record (admin and member)
  useEffect(() => {
    if ((isAdmin() || isMember()) && field?._id) {
      api
        .get(`/record/${field._id}/draft`)
        .then((res) => setDraftExists(res.data.exists))
        .catch(() => setDraftExists(false));
    }
  }, [field?._id, isAdmin, isMember]);

  // Admins: check whether the draft is identical to the current record
  const [draftIdentical, setDraftIdentical] = useState(false);
  useEffect(() => {
    setDraftIdentical(false);
    if (!isAdmin() || !draftExists || !field?._id) return;
    api
      .get(`/records-drafts/${field._id}`)
      .then((res) => setDraftIdentical(recordsIdentical(field, res.data)))
      .catch(() => setDraftIdentical(false));
  }, [field, draftExists, isAdmin]);

  // Create a draft and navigate to draft-edit
  const handleCreateDraft = async () => {
    try {
      await api.post(`/record/${field._id}/draft`);
      navigate("draft-edit");
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error("A draft already exists for this record");
      } else {
        toast.error("Failed to create draft");
      }
    }
  };

  // Helper to check if a tag name exists in the field's populated tags
  const hasTagByName = (tagName) => {
    if (!field?.tags || !Array.isArray(field.tags)) return false;
    return field.tags.some((tag) => {
      // Handle both populated objects and string values
      const name = typeof tag === "object" ? tag.name : tag;
      return name === tagName;
    });
  };

  const bgColor = hasTagByName("Under Review")
    ? "bg-yellow-50"
    : "bg-gray-200";

  const btnClass =
    "py-2 px-4 float-right text-center text-gray-100 font-bold text-lg border-2 border-black bg-cyan-900 hover:bg-cyan-800 hover:text-white rounded-lg transition duration-300";

  return (
    <article>
      <div className="flow-root mt-12 mb-12">
        {isAdmin() ? (
          <>
            {draftExists ? (
              <Link to="draft-edit" className={btnClass}>
                Edit Draft
              </Link>
            ) : (
              <button
                type="button"
                onClick={handleCreateDraft}
                className={btnClass}
              >
                Create Draft
              </button>
            )}
            <Form method={method}>
              <button type="submit" className={`${btnClass} mr-4`}>
                {draftExists ? "Delete Field and Draft" : "Delete Field"}
              </button>
            </Form>
          </>
        ) : isMember() && draftExists ? (
          <Link to="draft-edit" className={btnClass}>
            Show Draft
          </Link>
        ) : (
          <span></span>
        )}

        <Link
          to=".."
          className="py-2 px-3 float-right mr-4 text-center text-cyan-900 font-bold text-lg border-2 border-cyan-900 bg-white hover:bg-gray-300 hover:text-gray-800 rounded-lg transition duration-300"
        >
          Back
        </Link>
      </div>
      <div className="mb-2 flow root flex flex-wrap -mx-3">
        <div className="w-full max-w-48 md:w-3/12 px-3">
          <div className="block tracking-wide text-black text-2xl font-bold mb-2">
            {field.ofid}
          </div>
        </div>

        <div className="w-full md:w-9/12 px-3">
          <div className="block tracking-wide text-black text-2xl font-bold mb-2">
            {field.fieldName}
          </div>
        </div>
      </div>
      {(isAdmin() || isMember()) && draftExists && (
        <div className="mb-6 flex flex-wrap gap-y-2 -mx-3">
          <div className="px-3">
            <div className="inline-block bg-yellow-50 text-gray-700 text-sm border border-gray-700 rounded py-1 px-4 leading-tight">
              These are the current values. To see the planned changes,
              press &quot;{isAdmin() ? "Edit Draft" : "Show Draft"}&quot;.
            </div>
          </div>
          {isAdmin() && draftIdentical && (
            <div className="px-3">
              <div className="inline-block bg-yellow-50 text-gray-700 text-sm border border-gray-700 rounded py-1 px-4 leading-tight">
                There are no differences between the current field and the
                draft.
              </div>
            </div>
          )}
        </div>
      )}
      <div className="flex flex-wrap -mx-3">
        <div className="w-full md:w-1/6 px-3 flex flex-col">
          <div className="block uppercase tracking-wide text-gray-700 text-sm font-bold mb-2">
            Data Type
          </div>
          <div
            className={`mt-auto appearance-none block w-full min-h-12 ${bgColor} text-gray-700 border border-gray-700 rounded py-3 px-4 mb-3 leading-tight`}
          >
            {field.dataType}
          </div>
        </div>

        <div className="w-full md:w-1/6 px-3 flex flex-col">
          <div className="block uppercase tracking-wide text-gray-700 text-sm font-bold mb-2">
            Level
          </div>
          <div
            className={`mt-auto appearance-none block w-full min-h-12 ${bgColor} text-gray-700 border border-gray-700 rounded py-3 px-4 mb-3 leading-tight`}
          >
            {field.level}
          </div>
        </div>

        <div className="w-full md:w-1/6 px-3 flex flex-col">
          <div className="block uppercase tracking-wide text-gray-700 text-sm font-bold mb-2">
            Link Reference
          </div>
          <div
            className={`mt-auto appearance-none block w-full min-h-12 ${bgColor} text-gray-700 border border-gray-700 rounded py-3 px-4 mb-3 leading-tight break-words`}
          >
            {field.linkReference}
          </div>
        </div>

        <div className="w-full md:w-1/6 px-3 flex flex-col">
          <div className="block uppercase tracking-wide text-gray-700 text-sm font-bold mb-2">
            Introduced in Version
          </div>
          <div
            className={`mt-auto appearance-none block w-full min-h-12 ${bgColor} text-gray-700 border border-gray-700 rounded py-3 px-4 mb-3 leading-tight`}
          >
            {Array.isArray(field.introduced)
              ? field.introduced.join(".")
              : ""}
          </div>
        </div>

        <div className="w-full md:w-1/6 px-3 flex flex-col">
          <div className="block uppercase tracking-wide text-gray-700 text-sm font-bold mb-2">
            Valid until Version
          </div>
          <div
            className={`mt-auto appearance-none block w-full min-h-12 ${bgColor} text-gray-700 border border-gray-700 rounded py-3 px-4 mb-3 leading-tight`}
          >
            {Array.isArray(field.deprecated)
              ? field.deprecated.join(".")
              : ""}
          </div>
        </div>

        <div className="w-full md:w-1/6 px-3 flex flex-col">
          <div className="block uppercase tracking-wide text-gray-700 text-sm font-bold mb-2">
            Tags
          </div>
          <div className="mt-auto mb-3">
            <TagSelectDropdown
              availableTags={field.tags || []}
              selectedTags={field.tags || []}
              onSelectionChange={() => {}}
              labelPrefix="Tags"
              bgColor={bgColor}
              readOnly={true}
            />
          </div>
        </div>
      </div>
      {field.standardRefs?.length > 0 && (
        <div className="flex flex-wrap -mx-3">
          <div className="w-full px-3">
            <div className="block uppercase tracking-wide text-gray-700 text-sm font-bold mb-2">
              Standard References
            </div>
            <div className="mb-3">
              <StandardRefsDropdown
                standardRefs={field.standardRefs}
                bgColor={bgColor}
                readOnly={true}
              />
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-wrap -mx-3">
        <div className="w-full px-3">
          <div className="block uppercase tracking-wide text-gray-700 text-sm font-bold mb-2">
            Values
          </div>
          <div
            className={`appearance-none block w-full min-h-12 ${bgColor} text-gray-700 border border-gray-700 rounded py-3 px-4 mb-3 leading-tight whitespace-pre-line`}
          >
            {field.values}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap -mx-3">
        <div className="w-full px-3">
          <div className="block uppercase tracking-wide text-gray-700 text-sm font-bold mb-2">
            Example
          </div>
          <div
            className={`appearance-none block w-full min-h-12 ${bgColor} text-gray-700 border border-gray-700 rounded py-3 px-4 mb-3 leading-tight whitespace-pre-line`}
          >
            {field.example}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap -mx-3">
        <div className="w-full px-3">
          <div className="block uppercase tracking-wide text-gray-700 text-sm font-bold mb-2">
            Description
          </div>
          <div
            className={`appearance-none block w-full min-h-12 ${bgColor} text-gray-700 border border-gray-700 rounded py-3 px-4 mb-3 leading-tight whitespace-pre-line`}
          >
            {field.description}
          </div>
        </div>
      </div>
      {field.uploadedFile && (
        <div className="flex flex-wrap -mx-3">
          <div className="w-full px-3">
            <div className="block uppercase tracking-wide text-gray-700 text-sm font-bold mb-2">
              Image
            </div>
            <div
              className={`appearance-none block w-full min-h-12 ${bgColor} text-gray-700 border border-gray-700 rounded py-3 px-4 mb-3 leading-tight`}
            >
              <img
                src={`${ASSET_BASE_URL}/uploads/${field.uploadedFile}`}
                alt={field.uploadedFile}
                className="max-w-full h-auto"
              />
            </div>
          </div>
        </div>
      )}

      {/* pagination */}
      <div className="flex items-center justify-end mt-2 gap-2">
        <button
          onClick={goToFirst}
          disabled={currentRecord <= 0}
          className="p-1 border border-gray-300 px-2 disabled:opacity-30"
        >
          {"<<"}
        </button>
        <button
          onClick={goToPrev}
          disabled={currentRecord <= 0}
          className="p-1 border border-gray-300 px-2 disabled:opacity-30"
        >
          {"<"}
        </button>
        <button
          onClick={goToNext}
          disabled={currentRecord >= fields.length - 1}
          className="p-1 border border-gray-300 px-2 disabled:opacity-30"
        >
          {">"}
        </button>
        <button
          onClick={goToLast}
          disabled={currentRecord >= fields.length - 1}
          className="p-1 border border-gray-300 px-2 disabled:opacity-30"
        >
          {">>"}
        </button>
      </div>
    </article>
  );
}

FieldDetail.propTypes = {
  method: PropTypes.string,
  field: recordPropType.isRequired,
};
